import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";

import { FilterBar } from "@/components/data";
import type { FilterSpec } from "@/components/data";
import type { FilterValues } from "@/lib/filters";
import { FreshnessLine } from "@/components/shell/FreshnessLine";
import type { FreshnessMeta } from "@/lib/api";
import { AlertCard } from "@/modules/protex/components/AlertCard";

import {
  getPeriodWeek,
  getPeriods,
  getWeek,
  getWeeks,
  messageFor,
  periodWeekKey,
  periodsKey,
  weekKey,
  weeksKey,
  type Grain,
  type PipelineTile,
  type WeekData,
} from "./api";
import { FixFirstCard } from "./components/FixFirstCard";
import { GrainToggle } from "./components/GrainToggle";
import { orderLaneWithGates } from "./components/laneOrder";
import { formatPeriodLabel, periodNoun, readGrain, readPeriod } from "./components/period";
import { Track } from "./components/Track";

/**
 * The tracks a week's tiles are laid out into, in the UX-spec order (front
 * doors, needs and routing, Cortex, Protex, Vox, outputs). `s01_topic_sourcing`
 * is the "front doors" track on its own; the rest of the frontdoor lane
 * (`s02`/`s03`/`g0`) is "Needs and routing" — the two are one CVCE `lane` but
 * two reader-facing tracks (UX spec §4/§6).
 */
function groupTracks(tiles: PipelineTile[]) {
  const sorted = [...tiles].sort((a, b) => a.stage_order - b.stage_order);
  const frontDoors = sorted.filter((t) => t.stage_key.startsWith("s01"));
  const needsRouting = orderLaneWithGates(
    sorted.filter(
      (t) => t.stage_key.startsWith("s02") || t.stage_key.startsWith("s03") || t.stage_key.startsWith("g0"),
    ),
  );
  const cortex = orderLaneWithGates(sorted.filter((t) => t.lane === "cortex"));
  const protex = orderLaneWithGates(sorted.filter((t) => t.lane === "protex"));
  // The library output (o4_blueprint_publish) reads as a branch off Vox
  // ("from 11: Blueprint published to library") rather than a step of its own —
  // it is an output (lane "outputs"), never part of the vox lane's own tiles.
  const vox = orderLaneWithGates(sorted.filter((t) => t.lane === "vox"));
  const blueprintPublish = sorted.find((t) => t.stage_key === "o4_blueprint_publish") ?? null;
  const outputsCx = sorted.filter((t) => t.lane === "outputs" && t.output_row === "cx");
  const outputsMarketing = sorted.filter((t) => t.lane === "outputs" && t.output_row === "marketing");
  return { frontDoors, needsRouting, cortex, protex, vox, blueprintPublish, outputsCx, outputsMarketing };
}

/**
 * A uniform `{period_start, complete}[]` list regardless of grain — week
 * grain still reads the deployed `/weeks` alias (untouched cache key, so
 * spec-1/2's existing fixtures and tests keep working unchanged); month
 * grain reads the new `/periods?grain=month` route.
 */
function usePeriodsForGrain(grain: Grain) {
  const weeks = useQuery({ queryKey: weeksKey, queryFn: getWeeks, enabled: grain === "week" });
  const months = useQuery({ queryKey: periodsKey("month"), queryFn: () => getPeriods("month"), enabled: grain === "month" });

  if (grain === "month") {
    return {
      isLoading: months.isLoading,
      isError: months.isError,
      error: months.error,
      available: months.data?.data.available,
      periods: months.data?.data.periods ?? [],
      defaultPeriod: months.data?.data.default_period ?? null,
      meta: months.data?.meta,
    };
  }
  return {
    isLoading: weeks.isLoading,
    isError: weeks.isError,
    error: weeks.error,
    available: weeks.data?.data.available,
    periods: (weeks.data?.data.weeks ?? []).map((w) => ({ period_start: w.week_start, complete: w.complete })),
    defaultPeriod: weeks.data?.data.default_week ?? null,
    meta: weeks.data?.meta,
  };
}

/** The tile data for one period, regardless of grain — same cache-key contract as `usePeriodsForGrain`. */
function useWeekForGrain(grain: Grain, periodStart: string | null) {
  const week = useQuery({
    queryKey: weekKey(periodStart ?? "pending"),
    queryFn: () => getWeek(periodStart as string),
    enabled: grain === "week" && Boolean(periodStart),
  });
  const month = useQuery({
    queryKey: periodWeekKey("month", periodStart ?? "pending"),
    queryFn: () => getPeriodWeek("month", periodStart as string),
    enabled: grain === "month" && Boolean(periodStart),
  });
  return grain === "month" ? month : week;
}

export function PipelineTab({ areaName }: { areaName: string }) {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate();

  const grain = readGrain(search);
  const periods = usePeriodsForGrain(grain);
  const requestedPeriod = readPeriod(search, grain);
  const defaultPeriod = periods.defaultPeriod;
  const periodStart = requestedPeriod ?? defaultPeriod;

  const weekQuery = useWeekForGrain(grain, periodStart);

  const openStage = (stageKey: string) => {
    void navigate({ to: ".", search: { ...search, grain, period: periodStart, stage: stageKey }, replace: false });
  };

  const setGrain = (nextGrain: Grain) => {
    const next: Record<string, unknown> = { ...search, grain: nextGrain };
    delete next.week;
    delete next.period;
    void navigate({ to: ".", search: next, replace: true });
  };

  const setPeriod = (next: FilterValues) => {
    const nextSearch: Record<string, unknown> = { ...search, grain, period: next.period };
    delete nextSearch.week;
    void navigate({ to: ".", search: nextSearch, replace: true });
  };

  if (periods.isLoading || (Boolean(periodStart) && weekQuery.isLoading)) {
    return (
      <div className="flex flex-col gap-3" aria-busy="true">
        <p role="status" className="sr-only">
          Loading the pipeline…
        </p>
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-24 animate-pulse rounded-ks-md bg-ks-grey-4/40" />
        ))}
      </div>
    );
  }

  if (periods.isError || weekQuery.isError || periods.available === false) {
    const error = periods.error ?? weekQuery.error;
    return (
      <AlertCard
        title="We couldn't load the pipeline"
        detail={error ? messageFor(error, areaName) : "Try again, or check back shortly."}
      />
    );
  }

  const periodSpec: FilterSpec = {
    name: "period",
    label: periodNoun(grain) === "month" ? "Month" : "Week",
    kind: "choice",
    options: periods.periods.map((p) => ({
      value: p.period_start,
      label: formatPeriodLabel(grain, p.period_start, p.complete),
    })),
  };

  const week = weekQuery.data?.data ?? null;

  if (!periodStart || !week || week.available === false) {
    return (
      <div className="flex flex-col gap-4">
        <GrainToggle grain={grain} onChange={setGrain} />
        <FilterBar
          layout="inline"
          filters={[periodSpec]}
          values={{ period: periodStart ?? "" }}
          defaultValues={{ period: defaultPeriod ?? "" }}
          onChange={setPeriod}
        />
        <p className="text-sm text-ks-ink">No pipeline data since we last checked.</p>
      </div>
    );
  }

  const tilesByKey = new Map(week.tiles.map((t) => [t.stage_key, t]));
  const isCurrentPeriod = week.complete === false;

  return (
    <PipelineTabBody
      grain={grain}
      week={week}
      tilesByKey={tilesByKey}
      isCurrentPeriod={isCurrentPeriod}
      periodSpec={periodSpec}
      periodStart={periodStart}
      defaultPeriod={defaultPeriod}
      onGrainChange={setGrain}
      onPeriodChange={setPeriod}
      onOpen={openStage}
      freshnessMeta={periods.meta}
      pending={weekQuery.isFetching}
    />
  );
}

function PipelineTabBody({
  grain,
  week,
  tilesByKey,
  isCurrentPeriod,
  periodSpec,
  periodStart,
  defaultPeriod,
  onGrainChange,
  onPeriodChange,
  onOpen,
  freshnessMeta,
  pending,
}: {
  grain: Grain;
  week: WeekData;
  tilesByKey: Map<string, PipelineTile>;
  isCurrentPeriod: boolean;
  periodSpec: FilterSpec;
  periodStart: string;
  defaultPeriod: string | null;
  onGrainChange: (grain: Grain) => void;
  onPeriodChange: (next: FilterValues) => void;
  onOpen: (stageKey: string) => void;
  freshnessMeta: FreshnessMeta | undefined;
  pending: boolean;
}) {
  const [staleOnly, setStaleOnly] = useState(false);
  const tracks = useMemo(() => groupTracks(week.tiles), [week.tiles]);
  const noun = periodNoun(grain);

  // "Show stale and no-feed only" keeps every gate (structure) but hides a
  // step tile that isn't grey for one of those two reasons (UX-mockup
  // prototype v2, frame 1 header toggle). Off by default — the full period.
  const keep = (tile: PipelineTile) =>
    tile.step === null || !staleOnly || tile.grey_reason === "stale" || tile.grey_reason === "no_feed";
  const filtered = staleOnly
    ? {
        ...tracks,
        frontDoors: tracks.frontDoors.filter(keep),
        needsRouting: tracks.needsRouting.filter(keep),
        cortex: tracks.cortex.filter(keep),
        protex: tracks.protex.filter(keep),
        vox: tracks.vox.filter(keep),
        outputsCx: tracks.outputsCx.filter(keep),
        outputsMarketing: tracks.outputsMarketing.filter(keep),
      }
    : tracks;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2.5">
        <GrainToggle grain={grain} onChange={onGrainChange} />
        <FilterBar
          layout="inline"
          filters={[periodSpec]}
          values={{ period: periodStart }}
          defaultValues={{ period: defaultPeriod ?? "" }}
          onChange={onPeriodChange}
        />
      </div>
      <FreshnessLine meta={freshnessMeta} pending={pending} />
      {isCurrentPeriod && (
        <p role="status" className="text-xs text-ks-grey-1">
          {noun === "month" ? "Month" : "Week"} in progress. Colours may change.
        </p>
      )}
      <FixFirstCard
        fixFirst={week.fix_first}
        tilesByKey={tilesByKey}
        onOpen={onOpen}
        weekStart={periodStart}
        noun={noun}
      />
      <div className="flex items-center justify-between gap-3">
        <p data-counts className="text-sm text-ks-ink">
          <b className="text-ks-fail">{week.counts.red} red</b> ·{" "}
          <b className="text-ks-warn">{week.counts.amber} amber</b> ·{" "}
          <b className="text-ks-pass">{week.counts.green} green</b> ·{" "}
          <span className="text-ks-grey-1">{week.counts.grey} grey</span>
        </p>
        <label className="flex items-center gap-1.5 text-xs text-ks-grey-1">
          <input
            type="checkbox"
            data-nonmutating
            checked={staleOnly}
            onChange={(e) => setStaleOnly(e.target.checked)}
            className="h-3.5 w-3.5 rounded border-ks-grey-3"
          />
          Show stale and no-feed only
        </label>
      </div>

      <Track title="Front doors" tiles={filtered.frontDoors} frontDoors={week.front_doors} onOpen={onOpen} />
      <Track title="Needs and routing" tiles={filtered.needsRouting} frontDoors={week.front_doors} onOpen={onOpen} />
      <Track title="Cortex" tiles={filtered.cortex} onOpen={onOpen} />
      <Track title="Protex" tiles={filtered.protex} onOpen={onOpen} />
      <Track
        title="Vox"
        tiles={filtered.vox}
        onOpen={onOpen}
        note={tracks.blueprintPublish ? "from 11:" : undefined}
        branchTile={
          tracks.blueprintPublish && (!staleOnly || keep(tracks.blueprintPublish))
            ? tracks.blueprintPublish
            : undefined
        }
      />
      <section
        aria-labelledby="track-outputs"
        className="flex flex-col gap-2 rounded-ks-md border border-ks-grey-4 bg-ks-purple-4/40 p-3"
      >
        <h3 id="track-outputs" className="text-xs font-bold tracking-wider text-ks-purple uppercase">
          Outputs
        </h3>
        <Track title="CX" tiles={filtered.outputsCx} onOpen={onOpen} outputs />
        <Track title="Marketing" tiles={filtered.outputsMarketing} onOpen={onOpen} outputs />
      </section>
      <p className="text-[11px] text-ks-grey-1">
        Numbers are pipeline steps in order. Q quality · T time · F flow · R rework. {"◇"} is a gate. Each
        step opens its detail.
      </p>
    </div>
  );
}
